import { useEffect, useState } from "react";

import MessageChatSquareIcon from "@uui/MessageChatSquare";
import { isEmpty, map, times } from "lodash/fp";

import { Skeleton, Typography } from "@mui/material";

import { IconHeadingIcon } from "@app/analytics/components/IconHeading";
import type { PulseTopic } from "@app/analytics/Pulse/types";
import * as Events from "@app/analytics/tracking/events";

import {
  EmptyStateContainer,
  EmptyStateIcon,
  EmptyStateSubtext,
  EmptyStateTitle,
} from "@rui/EmptyStateContainer";
import Stack, { Column, Row } from "@rui/Stack";

import { Table } from "./styles";
import TopicsTableRow from "./TopicsTableRow";
import { ModuleHeading } from "../styles";

export interface TopicsTableProps {
  topics: PulseTopic[];
  loading: boolean;
}

const LoadingTable = () => (
  <Stack spacing={1} sx={{ p: 1 }}>
    {times(
      (i) => (
        <Skeleton
          key={i}
          variant="rectangular"
          height={50}
          sx={{ borderRadius: "8px" }}
        />
      ),
      3
    )}
  </Stack>
);

const EmptyTable = () => (
  <Column sx={{ p: 1, pt: 0 }}>
    <EmptyStateContainer>
      <EmptyStateIcon Icon={MessageChatSquareIcon} />
      <EmptyStateTitle>You haven't had any meetings this week!</EmptyStateTitle>
      <EmptyStateSubtext>
        Add Read to your upcoming meetings to start generating topics.
      </EmptyStateSubtext>
    </EmptyStateContainer>
  </Column>
);

const getStableTopicIdsKey = (topics: TopicsTableProps["topics"]) =>
  map("id", topics).join("");

const TopicsTable = ({ topics, loading }: TopicsTableProps) => {
  const [expanded, setExpanded] = useState<PulseTopic["id"] | null>(null);

  useEffect(() => {
    if (isEmpty(topics)) return;
    setExpanded(topics[0].id);
  }, [getStableTopicIdsKey(topics)]);

  const toggleExpanded = (idToExpand: PulseTopic["id"]) => {
    if (idToExpand === expanded) {
      setExpanded(null);
    } else {
      Events.expandTopic();
      setExpanded(idToExpand);
    }
  };

  return (
    <Table>
      <ModuleHeading variant="apart" spacing={2} sx={{ px: 3 }}>
        <Row spacing={1}>
          <IconHeadingIcon type="topics" />
          <Column>
            <Typography variant="h3">Topics</Typography>
            <Typography variant="labelReg" color="shades.neutral.500">
              Topic progression from meetings you own or were invited to
            </Typography>
          </Column>
        </Row>
      </ModuleHeading>
      {loading ? (
        <LoadingTable />
      ) : isEmpty(topics) ? (
        <EmptyTable />
      ) : (
        topics.map((topic) => (
          <TopicsTableRow
            key={topic.id}
            expanded={expanded === topic.id}
            onToggleExpanded={toggleExpanded}
            topic={topic}
          />
        ))
      )}
    </Table>
  );
};

export default TopicsTable;
